import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGraduationCap,
  faCalendar,
  faBookOpen,
  faPills,
  faChartBar,
  faCheckCircle,
  faLock,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import Preloader from '../../../components/common/Preloader';
import MetaComponent from '../../../components/common/MetaComponent';
import { fetchExams } from '../../../services/exams.util';
import {
  getExamPurchaseInfo,
  createExamPurchaseOrder,
  purchaseExam
} from '../../../services/purchase.util';
import { initializeRazorpayPayment } from '../../../services/razorpay.util';
import { useAuthContext } from '../../../context/Context';
import { useMessageBox } from '../../../components/others/MessageBox';
import AOS from 'aos';

const RENEWAL_MESSAGE = 'Your subscription has ended. Please renew it to continue accessing this content.';

const ExamsListPage = () => {
  const { loggedIn, loggedInUser } = useAuthContext();
  const { showMessage } = useMessageBox();
  const [searchParams] = useSearchParams();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchaseInfo, setPurchaseInfo] = useState({});
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Get the type parameter from URL (materials or tests)
  const contentType = searchParams.get('type');
  const selectedCourseSlug = searchParams.get('course');
  const displayedExams = selectedCourseSlug
    ? exams.filter((exam) => exam.slug === selectedCourseSlug)
    : exams;
  const selectedCourse = selectedCourseSlug ? displayedExams[0] : null;

  // Generate dynamic metadata based on content type
  const metadata = {
    title:
      selectedCourse
        ? `${selectedCourse.title} || GVK Student Portal`
        : contentType === 'materials'
        ? 'Study Materials || GVK Student Portal'
        : contentType === 'tests'
          ? 'Test Papers || GVK Student Portal'
          : 'All Exams || GVK Student Portal',
    description:
      contentType === 'materials'
        ? 'Access comprehensive study materials, notes, and resources for exam preparation.'
        : contentType === 'tests'
          ? 'Practice with mock tests, previous year questions, and full-length test papers.'
          : 'Browse all available exams and start your preparation journey with comprehensive study materials.',
  };

  // Fetch exams from API
  useEffect(() => {
    const loadExams = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Loading exams from API...');

        const examsData = await fetchExams();
        console.log('Exams loaded:', examsData?.length || 0, 'exams');
        setExams(examsData || []);
      } catch (error) {
        console.error('Error loading exams:', error);
        setError(error.message || 'Failed to load exams');
        setExams([]);
      } finally {
        setLoading(false);
      }
    };

    loadExams();
  }, []);

  // Fetch purchase info for all exams
  useEffect(() => {
    const loadPurchaseInfo = async () => {
      if (!loggedIn || exams.length === 0) return;

      try {
        setPurchaseLoading(true);
        const purchaseData = {};

        // Fetch purchase info for each exam in parallel
        await Promise.all(
          exams.map(async (exam) => {
            try {
              const info = await getExamPurchaseInfo(exam._id);
              purchaseData[exam._id] = info;
            } catch (error) {
              console.error(
                `Error fetching purchase info for exam ${exam._id}:`,
                error
              );
              // Set default values if fetch fails
              purchaseData[exam._id] = {
                exam: { ...exam, isPurchasable: true },
                userStatus: { hasPurchased: false, canAccess: false },
              };
            }
          })
        );

        setPurchaseInfo(purchaseData);
      } catch (error) {
        console.error('Error loading purchase info:', error);
      } finally {
        setPurchaseLoading(false);
      }
    };

    loadPurchaseInfo();
  }, [exams, loggedIn]);

  // Refresh AOS animations when data is loaded
  useEffect(() => {
    if (!loading && !purchaseLoading && displayedExams.length > 0) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        AOS.refresh();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, purchaseLoading, displayedExams.length]);

  // Get icon component based on icon name
  const getIconComponent = (iconName) => {
    const iconMap = {
      calendar: faCalendar,
      book: faBookOpen,
      pills: faPills,
      chart: faChartBar,
      graduation: faGraduationCap,
    };
    return iconMap[iconName] || faGraduationCap;
  };

  const handlePaymentSuccess = useCallback(async (response) => {
    try {
      showMessage('Verifying payment...', 'info');
      
      // Verify payment and complete purchase
      const purchaseResponse = await purchaseExam(response.id, {
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
        purchaseType: 'full',
      });

      if (purchaseResponse.success) {
        showMessage(`Payment successful! Exam purchased successfully.`, 'success');
      } else {
        throw new Error(purchaseResponse.message || 'Purchase verification failed');
      }
    } catch (error) {
      console.error('Purchase verification error:', error);
      showMessage(error.message || 'Payment verification failed', 'error');
    } finally {
      setProcessingPayment(false);
    }
  }, [showMessage]);

  // Handle Razorpay payment messages from React Native WebView
  useEffect(() => {
    const handleMessage = async(event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'RAZORPAY_SUCCESS') {
          await handlePaymentSuccess(data.data);
          const examsData = await fetchExams();
          setExams(examsData || []);
          setProcessingPayment(false);
        } else if (data.type === 'RAZORPAY_ERROR') {
          const errorMessage = data.error?.description || data.error?.message || 'Payment could not be processed';
          showMessage(`Payment failed: ${errorMessage}. Please try again or contact support if the issue persists.`, 'error');
          setProcessingPayment(false);
        }
      } catch (e) {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener('message', handleMessage);

    // Cleanup: remove event listener when component unmounts
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handlePaymentSuccess, showMessage]); // Include dependencies to avoid stale closures

  // Generate pricing tiers data for an exam
  const getPricingTiers = (exam, purchaseInfo) => {
    const examInfo = purchaseInfo?.exam || exam;
    const userStatus = purchaseInfo?.userStatus || {
      hasPurchased: false,
      canAccess: false,
    };

    // Get pricing from database
    const fullExamPrice = examInfo.price || 99;
    const fullExamDiscountPrice = examInfo.discountPrice;
    const testsOnlyPrice = examInfo.testsOnlyPrice;
    const testsOnlyDiscountPrice = examInfo.testsOnlyDiscountPrice;

    const tiers = [
      {
        id: 'full',
        title: 'Full Exam',
        price: fullExamDiscountPrice || fullExamPrice,
        originalPrice: fullExamDiscountPrice ? fullExamPrice : null,
        discount: fullExamDiscountPrice
          ? Math.round((1 - fullExamDiscountPrice / fullExamPrice) * 100)
          : null,
        features: null, // Features are now displayed under description
        isPurchased:
          userStatus.hasPurchased &&
          (userStatus.purchaseType === 'full' ||
            userStatus.accessLevel?.accessType === 'full'),
        buttonText: 'Buy Full Exam',
        buttonClass: 'full-exam-btn',
      },
    ];

    // Only add tests-only tier if testsOnlyPrice is explicitly set in database
    if (testsOnlyPrice && testsOnlyPrice > 0) {
      tiers.push({
        id: 'tests',
        title: 'Tests Only',
        price: testsOnlyDiscountPrice || testsOnlyPrice,
        originalPrice: testsOnlyDiscountPrice ? testsOnlyPrice : null,
        discount: testsOnlyDiscountPrice
          ? Math.round((1 - testsOnlyDiscountPrice / testsOnlyPrice) * 100)
          : null,
        features: [
          `${exam.testCount || '15+'} Full-length Tests`,
          'Comprehensive Answer Keys',
          'Performance Analytics',
          'Timed Test Environment',
          'Question-wise Analysis',
          'Previous Year Papers',
        ],
        isPurchased:
          userStatus.hasPurchased &&
          (userStatus.purchaseType === 'tests' ||
            userStatus.accessLevel?.accessType === 'tests'),
        buttonText: 'Buy Tests Only',
        buttonClass: 'tests-only-btn',
      });
    }

    return tiers;
  };

  // Handle purchase button click
  const handlePurchaseClick = async (exam, purchaseType = 'full') => {
    if (!loggedInUser) {
      showMessage('Please login to purchase exams', 'error');
      return;
    }

    if (processingPayment) {
      showMessage('Payment is already being processed', 'warning');
      return;
    }

    try {
      setProcessingPayment(true);
      const orderResponse = await createExamPurchaseOrder(exam._id, {
        purchaseType,
      });

      if (!orderResponse.success) {
        throw new Error(
          orderResponse.message || 'Failed to create payment order'
        );
      }

      const { order, razorpayKeyId, callbackUrl } = orderResponse.data;
      showMessage('Creating payment order...', 'info');
      const options = {
        key: razorpayKeyId,
        amount: order.amount,
        currency: order.currency,
        name: 'gvkEDU',
        description: `Purchase ${exam.title} - ${purchaseType === 'full' ? 'Full Exam' : 'Tests Only'}`,
        order_id: order.id,
        callback_url: callbackUrl,
        redirect: true,
        prefill: {
          contact: loggedInUser.mobileNumber || '',
          email: loggedInUser.email || '',
        },
        notes: {
          id: exam._id,
          examTitle: exam.title,
          purchaseType: purchaseType,
        },
        theme: {
          color: '#000000',
        },
        modal: {
          ondismiss: function () {
            setProcessingPayment(false);
            showMessage('Payment cancelled', 'warning');
          },
        },
      };
      // Initialize Razorpay payment using utility
      // Note: With callback_url, payment success/failure is handled via redirect
      // The callback handler will redirect to /payment-result page
      await initializeRazorpayPayment(options, (error) => {
        throw error;
      });

      // Reset processing state when modal is closed (user cancels)
      // Payment success/failure will be handled by callback redirect
    } catch (error) {
      console.error('Purchase error:', error);
      showMessage(error.message || 'Failed to initiate payment', 'error');
      setProcessingPayment(false);
    }
  };

  return (
    <div className='main-content'>
      <style>{`
        .coursesCard__pricing {
          margin-top: auto;
          padding: 0 20px 15px 20px !important;
        }
        
        .pricing-tiers-container {
          padding: 20px 20px 25px 20px;
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          margin-top: auto;
        }
        
        .pricing-tier-card {
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.15);
          margin-bottom: 16px;
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
          min-height: 300px;
          padding-bottom: 70px;
        }
        
        .pricing-tier-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
        }
        
        .pricing-tier-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
          border-color: rgba(255, 255, 255, 0.25);
          background: rgba(0, 0, 0, 0.6);
        }
        
        .pricing-tier-card:last-child {
          margin-bottom: 0;
        }
        
        .tier-header {
          padding: 20px 20px 10px 20px;
          text-align: left;
          position: relative;
        }
        
        .tier-title {
          font-size: 20px !important;
          font-weight: 600 !important;
          color: rgba(255, 255, 255, 0.95) !important;
          margin-bottom: 8px !important;
        }
        
        .tier-price {
          font-size: 36px !important;
          font-weight: 700 !important;
          color: #4285F4 !important;
          margin-bottom: 4px !important;
        }
        
        .tier-original-price {
          font-size: 16px !important;
          color: rgba(255, 255, 255, 0.5) !important;
          text-decoration: line-through !important;
          margin-left: 8px;
        }
        
        .tier-discount-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background: linear-gradient(135deg, #ff4757, #ff3838) !important;
          color: white !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          padding: 6px 10px !important;
          border-radius: 12px !important;
          transform: rotate(12deg);
          box-shadow: 0 4px 16px rgba(255, 71, 87, 0.5);
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
          z-index: 10;
          animation: discountPulse 2s ease-in-out infinite;
        }
        
        @keyframes discountPulse {
          0%, 100% {
            transform: rotate(12deg) scale(1);
            box-shadow: 0 4px 16px rgba(255, 71, 87, 0.5);
          }
          50% {
            transform: rotate(12deg) scale(1.05);
            box-shadow: 0 6px 20px rgba(255, 71, 87, 0.7);
          }
        }
        
        .tier-features {
          padding: 0 20px 20px 20px;
        }
        
        .tier-feature {
          display: flex;
          align-items: flex-start;
          margin-bottom: 10px;
          font-size: 14px !important;
          color: rgba(255, 255, 255, 0.75) !important;
          line-height: 1.5;
        }
        
        .tier-feature-icon {
          color: #10b981 !important;
          margin-right: 10px;
          font-size: 12px !important;
          margin-top: 2px;
          flex-shrink: 0;
        }
        
        .tier-purchase-btn {
          margin: 0 20px 20px 20px;
          padding: 18px 36px !important;
          border-radius: 50px !important;
          font-weight: 700 !important;
          font-size: 15px !important;
          letter-spacing: 0.8px !important;
          text-transform: uppercase !important;
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          width: calc(100% - 40px);
          cursor: pointer;
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          position: relative;
          overflow: hidden;
          min-width: 200px;
          color: #ffffff !important;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          will-change: transform, box-shadow;
        }
        
        .tier-purchase-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }
        
        .tier-purchase-btn.full-exam-btn {
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.8) 0%, 
            rgba(15, 15, 15, 0.7) 50%,
            rgba(0, 0, 0, 0.8) 100%
          ) !important;
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          font-weight: 700 !important;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                      0 0 20px rgba(255, 255, 255, 0.2),
                      inset 0 2px 0 rgba(255, 255, 255, 0.15),
                      inset 0 -1px 0 rgba(0, 0, 0, 0.2) !important;
        }
        
        .tier-purchase-btn.full-exam-btn:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.15) 0%, 
            rgba(0, 0, 0, 0.9) 50%,
            rgba(255, 255, 255, 0.1) 100%
          ) !important;
          color: #ffffff !important;
          transform: translateY(-6px) scale(1.08);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6), 
                      0 0 50px rgba(255, 255, 255, 0.3),
                      inset 0 2px 0 rgba(255, 255, 255, 0.25),
                      inset 0 -2px 0 rgba(0, 0, 0, 0.3) !important;
          border: 2px solid rgba(255, 255, 255, 0.5) !important;
        }
        
        .tier-purchase-btn.tests-only-btn {
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.8) 0%, 
            rgba(15, 15, 15, 0.7) 50%,
            rgba(0, 0, 0, 0.8) 100%
          ) !important;
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          font-weight: 700 !important;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                      0 0 20px rgba(255, 255, 255, 0.2),
                      inset 0 2px 0 rgba(255, 255, 255, 0.15),
                      inset 0 -1px 0 rgba(0, 0, 0, 0.2) !important;
        }
        
        .tier-purchase-btn.tests-only-btn:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.15) 0%, 
            rgba(0, 0, 0, 0.9) 50%,
            rgba(255, 255, 255, 0.1) 100%
          ) !important;
          color: #ffffff !important;
          transform: translateY(-6px) scale(1.08);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6), 
                      0 0 50px rgba(255, 255, 255, 0.3),
                      inset 0 2px 0 rgba(255, 255, 255, 0.25),
                      inset 0 -2px 0 rgba(0, 0, 0, 0.3) !important;
          border: 2px solid rgba(255, 255, 255, 0.5) !important;
        }
        
        .tier-purchase-btn:disabled {
          opacity: 0.6 !important;
          cursor: not-allowed !important;
          transform: none !important;
        }
        
        .purchased-tier {
          background: rgba(66, 133, 244, 0.15) !important;
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          border: 1px solid rgba(66, 133, 244, 0.3) !important;
          color: white !important;
          box-shadow: 0 4px 16px rgba(66, 133, 244, 0.2);
          position: relative;
          overflow: hidden;
        }
        
        .purchased-tier::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(66, 133, 244, 0.5), transparent);
        }
        
        .purchased-tier .tier-title,
        .purchased-tier .tier-price {
          color: rgba(255, 255, 255, 0.95) !important;
        }
        
        .purchased-tier .tier-feature {
          color: rgba(255, 255, 255, 0.85) !important;
        }
        
        .purchased-tier .tier-feature-icon {
          color: #4285F4 !important;
        }

        .upgrade-option {
          background: rgba(255, 255, 255, 0.05) !important;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .upgrade-option::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }

        .upgrade-option::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, rgba(66, 133, 244, 0.1) 0%, rgba(66, 133, 244, 0.05) 50%, rgba(255, 255, 255, 0.1) 100%);
          opacity: 0.6;
          pointer-events: none;
        }

        .upgrade-badge {
          background: rgba(66, 133, 244, 0.9) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          color: white !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          padding: 6px 12px !important;
          border-radius: 12px !important;
          transform: rotate(8deg);
          box-shadow: 0 4px 16px rgba(66, 133, 244, 0.4) !important;
          border: 1px solid rgba(255, 255, 255, 0.3) !important;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
          z-index: 10;
          animation: upgradeGlow 3s ease-in-out infinite;
          letter-spacing: 0.5px;
        }

        @keyframes upgradeGlow {
          0%, 100% {
            transform: rotate(8deg) scale(1);
            box-shadow: 0 4px 16px rgba(66, 133, 244, 0.4);
          }
          50% {
            transform: rotate(8deg) scale(1.05);
            box-shadow: 0 6px 20px rgba(66, 133, 244, 0.6);
          }
        }

        .upgrade-feature {
          color: rgba(255, 255, 255, 0.85) !important;
        }

        .upgrade-feature .tier-feature-icon {
          color: #4285F4 !important;
        }

        .tier-purchase-btn.purchased {
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.8) 0%, 
            rgba(15, 15, 15, 0.7) 50%,
            rgba(0, 0, 0, 0.8) 100%
          ) !important;
          color: #ffffff !important;
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          padding: 18px 36px !important;
          margin: 0 20px 20px 20px !important;
          width: calc(100% - 40px) !important;
          min-height: 52px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          text-decoration: none !important;
          border-radius: 50px !important;
          font-size: 15px !important;
          font-weight: 700 !important;
          letter-spacing: 0.8px !important;
          text-transform: uppercase !important;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          backdrop-filter: blur(15px) !important;
          -webkit-backdrop-filter: blur(15px) !important;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                      0 0 20px rgba(255, 255, 255, 0.2),
                      inset 0 2px 0 rgba(255, 255, 255, 0.15),
                      inset 0 -1px 0 rgba(0, 0, 0, 0.2) !important;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          will-change: transform, box-shadow;
        }

        .tier-purchase-btn.purchased::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }

        .tier-purchase-btn.purchased:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.15) 0%, 
            rgba(0, 0, 0, 0.9) 50%,
            rgba(255, 255, 255, 0.1) 100%
          ) !important;
          color: #ffffff !important;
          transform: translateY(-6px) scale(1.08);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6), 
                      0 0 50px rgba(255, 255, 255, 0.3),
                      inset 0 2px 0 rgba(255, 255, 255, 0.25),
                      inset 0 -2px 0 rgba(0, 0, 0, 0.3) !important;
          border: 2px solid rgba(255, 255, 255, 0.5) !important;
          text-decoration: none !important;
        }

        .tier-purchase-btn.purchased:active {
          transform: translateY(-4px) scale(1.05);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), 
                      0 0 40px rgba(255, 255, 255, 0.25) !important;
        }

        .tier-purchase-btn.purchased:focus {
          text-decoration: none !important;
          outline: none;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                      0 0 20px rgba(255, 255, 255, 0.2),
                      0 0 0 3px rgba(255, 255, 255, 0.3) !important;
        }

        .tier-purchase-btn.upgrade {
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.8) 0%, 
            rgba(15, 15, 15, 0.7) 50%,
            rgba(0, 0, 0, 0.8) 100%
          ) !important;
          color: #ffffff !important;
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          font-weight: 700 !important;
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          position: relative;
          overflow: hidden;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                      0 0 20px rgba(255, 255, 255, 0.2),
                      inset 0 2px 0 rgba(255, 255, 255, 0.15),
                      inset 0 -1px 0 rgba(0, 0, 0, 0.2);
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          will-change: transform, box-shadow;
        }

        .tier-purchase-btn.upgrade::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }

        .tier-purchase-btn.upgrade:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.15) 0%, 
            rgba(0, 0, 0, 0.9) 50%,
            rgba(255, 255, 255, 0.1) 100%
          ) !important;
          color: #ffffff !important;
          transform: translateY(-6px) scale(1.08);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6), 
                      0 0 50px rgba(255, 255, 255, 0.3),
                      inset 0 2px 0 rgba(255, 255, 255, 0.25),
                      inset 0 -2px 0 rgba(0, 0, 0, 0.3);
          border: 2px solid rgba(255, 255, 255, 0.5) !important;
        }
        
        /* Section Title for alternative options */
        .purchase-section-title {
          padding: 15px 20px 0 20px;
          font-size: 14px !important;
          font-weight: 600 !important;
          color: rgba(255, 255, 255, 0.7) !important;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .purchase-features-list {
          padding: 10px 20px 5px 20px;
        }
        
        /* Price Display Section */
        .price-display-section {
          padding: 20px 20px 10px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .price-info-left {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .price-main-row {
          display: flex;
          align-items: baseline;
          gap: 12px;
        }
        
        .price-current {
          font-size: 24px !important;
          font-weight: 700 !important;
          color: #ffffff !important;
          line-height: 1 !important;
          text-shadow: 0 2px 12px rgba(255, 255, 255, 0.3);
        }
        
        .price-original {
          font-size: 16px !important;
          color: rgba(255, 255, 255, 0.4) !important;
          text-decoration: line-through !important;
          font-weight: 500 !important;
        }
        
        .price-savings {
          font-size: 12px !important;
          color: #10b981 !important;
          font-weight: 600 !important;
        }
        
        .price-discount-badge {
          display: inline-block;
          font-size: 11px !important;
          font-weight: 700 !important;
          color: #10b981 !important;
          background: rgba(16, 185, 129, 0.15) !important;
          padding: 4px 10px !important;
          border-radius: 6px !important;
          letter-spacing: 0.5px !important;
          border: 1px solid rgba(16, 185, 129, 0.3) !important;
        }
        
        .price-discount-badge.upgrade-badge {
          color: #10b981 !important;
          background: rgba(16, 185, 129, 0.2) !important;
          border: 1px solid rgba(16, 185, 129, 0.4) !important;
        }
        
        /* Simple Action Button */
        .simple-purchase-btn {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px;
          padding: 16px 32px !important;
          margin: 0 20px 20px 20px !important;
          width: calc(100% - 40px) !important;
          position: relative;
          overflow: hidden !important;
          border-radius: 12px !important;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.15) 0%, 
            rgba(255, 255, 255, 0.10) 100%
          ) !important;
          border: 1.5px solid rgba(255, 255, 255, 0.3) !important;
          backdrop-filter: blur(20px) !important;
          -webkit-backdrop-filter: blur(20px) !important;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3),
                      inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          font-size: 15px !important;
          font-weight: 600 !important;
          letter-spacing: 0.3px !important;
          color: rgba(255, 255, 255, 0.95) !important;
          text-transform: none !important;
        }
        
        .simple-purchase-btn:hover {
          transform: translateY(-2px) !important;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.22) 0%, 
            rgba(255, 255, 255, 0.15) 100%
          ) !important;
          border: 1.5px solid rgba(255, 255, 255, 0.5) !important;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4),
                      inset 0 1px 0 rgba(255, 255, 255, 0.3),
                      0 0 20px rgba(255, 255, 255, 0.15) !important;
        }
        
        .simple-purchase-btn:active {
          transform: translateY(0px) !important;
        }

        .simple-purchase-btn.stacked-action-btn {
          margin-bottom: 10px !important;
        }

        .simple-purchase-btn.view-subjects-btn {
          margin-top: 0 !important;
          text-decoration: none !important;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.08) 0%, 
            rgba(255, 255, 255, 0.05) 100%
          ) !important;
          border: 1.5px solid rgba(255, 255, 255, 0.22) !important;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12) !important;
        }

        .simple-purchase-btn.view-subjects-btn:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.14) 0%, 
            rgba(255, 255, 255, 0.09) 100%
          ) !important;
          border: 1.5px solid rgba(255, 255, 255, 0.35) !important;
        }
        
        /* Upgrade button variant */
        .simple-purchase-btn.upgrade {
          background: linear-gradient(135deg, 
            rgba(16, 185, 129, 0.20) 0%, 
            rgba(16, 185, 129, 0.12) 100%
          ) !important;
          border: 1.5px solid rgba(16, 185, 129, 0.5) !important;
        }
        
        .simple-purchase-btn.upgrade:hover {
          background: linear-gradient(135deg, 
            rgba(16, 185, 129, 0.30) 0%, 
            rgba(16, 185, 129, 0.20) 100%
          ) !important;
          border: 1.5px solid rgba(16, 185, 129, 0.7) !important;
          box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3),
                      inset 0 1px 0 rgba(255, 255, 255, 0.2),
                      0 0 30px rgba(16, 185, 129, 0.2) !important;
        }
        
        @media (max-width: 768px) {
          .price-display-section {
            padding: 18px 15px 8px 15px;
          }
          
          .price-current {
            font-size: 22px !important;
          }
          
          .price-original {
            font-size: 14px !important;
          }
          
          .price-savings {
            font-size: 11px !important;
          }
          
          .price-discount-badge {
            font-size: 10px !important;
            padding: 3px 8px !important;
          }
          
          .simple-purchase-btn {
            padding: 14px 28px !important;
            margin: 0 15px 15px 15px !important;
            width: calc(100% - 30px) !important;
            font-size: 14px !important;
          }
        }
        
        @media (max-width: 576px) {
          .price-display-section {
            padding: 16px 12px 8px 12px;
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
          
          .price-current {
            font-size: 20px !important;
          }
          
          .price-original {
            font-size: 13px !important;
          }
          
          .price-savings {
            font-size: 10px !important;
          }
          
          .simple-purchase-btn {
            padding: 13px 24px !important;
            margin: 0 12px 12px 12px !important;
            width: calc(100% - 24px) !important;
            font-size: 13px !important;
          }
        }
        
        .purchased-indicator {
          background: rgba(16, 185, 129, 0.2) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          color: rgba(255, 255, 255, 0.95) !important;
          padding: 12px 20px !important;
          border-radius: 12px !important;
          font-weight: 600 !important;
          margin: 0 20px 20px 20px;
          text-align: center;
          font-size: 14px !important;
          border: 1px solid rgba(16, 185, 129, 0.3);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
          position: relative;
          overflow: hidden;
        }
        
        .purchased-indicator::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }
        
        .free-exam-status {
          background: rgba(59, 130, 246, 0.6) !important;
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          border: 1px solid rgba(59, 130, 246, 0.3) !important;
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2);
        }
        
        .free-exam-status .bg-info {
          background: transparent !important;
        }
        
        .coursesCard {
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 28px;
          overflow: hidden;
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.85) 0%, 
            rgba(20, 20, 20, 0.8) 50%, 
            rgba(0, 0, 0, 0.85) 100%
          );
          backdrop-filter: blur(25px) saturate(180%);
          -webkit-backdrop-filter: blur(25px) saturate(180%);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 
                      0 0 0 1px rgba(255, 255, 255, 0.1),
                      inset 0 1px 0 rgba(255, 255, 255, 0.1);
          position: relative;
          cursor: pointer;
          will-change: transform, box-shadow;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        
        .coursesCard::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }
        
        .coursesCard::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.1) 0%, 
            rgba(255, 255, 255, 0.05) 50%, 
            rgba(255, 255, 255, 0.1) 100%
          );
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
        }
        
        .coursesCard:hover {
          transform: translateY(-20px) scale(1.03);
          box-shadow: 0 50px 100px rgba(0, 0, 0, 0.6), 
                      0 0 80px rgba(255, 255, 255, 0.15), 
                      0 0 0 2px rgba(255, 255, 255, 0.3),
                      inset 0 1px 0 rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.4);
        }
        
        .coursesCard:hover::after {
          opacity: 1;
        }
        
        .coursesCard__content {
          color: rgba(255, 255, 255, 0.85) !important;
          padding: 20px 30px 20px 30px !important;
        }
        
        .coursesCard__content .coursesCard__text {
          color: rgba(255, 255, 255, 0.7) !important;
          margin-bottom: 0 !important;
          line-height: 1.5 !important;
        }
        
        .exam-features-list {
          padding: 0;
          margin-top: 15px;
        }
        
        .coursesCard__title {
          color: white !important;
        }
        
        .text-light-1 {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        
        .coursesCard__footer {
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          padding: 20px 30px 25px 30px !important;
        }
        
        .coursesCard__footer .text-14 {
          color: rgba(255, 255, 255, 0.9) !important;
          font-weight: 600 !important;
        }
        
        .coursesCard__footer .text-purple-1 {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        
        .coursesCard__footer .badge {
          font-size: 8px !important;
          font-weight: 700 !important;
          letter-spacing: 0.3px;
          text-transform: uppercase;
          animation: pulse 2s infinite;
          padding: 3px 6px !important;
        }
        
        .coursesCard__footer .badge.bg-success {
          background: linear-gradient(135deg, #10b981, #059669) !important;
          color: white !important;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
          font-size: 10px !important;
          padding: 4px 8px !important;
          font-weight: 700 !important;
        }
        
        .coursesCard__footer .badge.bg-warning {
          background: linear-gradient(135deg, #f59e0b, #d97706) !important;
          color: white !important;
          box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
          font-size: 10px !important;
          padding: 4px 8px !important;
          font-weight: 700 !important;
        }
        
        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
          100% {
            opacity: 1;
          }
        }
        
        .coursesCard__header {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: 20px 20px 0 0;
          padding: 30px !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .coursesCard__header::before {
          content: '';
          position: absolute;
          top: 20px;
          right: 30px;
          width: 60px;
          height: 60px;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          border: 1px solid rgba(16, 185, 129, 0.3);
          backdrop-filter: blur(0px);
        }
        
        .coursesCard__header::after {
          content: '';
          position: absolute;
          top: 40px;
          right: 60px;
          width: 40px;
          height: 40px;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%);
          border-radius: 50%;
          border: 1px solid rgba(16, 185, 129, 0.2);
          backdrop-filter: blur(0px);
        }
        
        .coursesCard__header .coursesCard__icon {
          background: rgba(16, 185, 129, 0.15) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(16, 185, 129, 0.3) !important;
          color: #34d399 !important;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
        }
        
        .coursesCard__header .coursesCard__title {
          color: white !important;
        }
        
        .coursesCard__header .text-light-1 {
          color: rgba(255, 255, 255, 0.7) !important;
        }
        
        @media (max-width: 768px) {
          .pricing-tiers-container {
            padding: 0 15px 15px 15px;
          }
          
          .pricing-tier-card {
            margin-bottom: 15px;
          }
          
          .tier-header {
            padding: 15px 15px 8px 15px;
          }
          
          .tier-title {
            font-size: 16px !important;
          }
          
          .tier-price {
            font-size: 26px !important;
          }
          
          .tier-original-price {
            font-size: 14px !important;
          }
          
          .tier-features {
            padding: 0 15px 15px 15px;
          }
          
          .tier-feature {
            font-size: 12px !important;
            margin-bottom: 10px;
          }
          
          .tier-purchase-btn {
            margin: 0 15px 15px 15px;
            padding: 10px 20px !important;
            font-size: 12px !important;
            width: calc(100% - 30px);
          }
          
          .coursesCard__pricing {
            padding: 0 15px 10px 15px !important;
          }
          
          .coursesCard__footer {
            padding: 12px 20px 15px 20px !important;
          }
          
          .coursesCard__footer .text-14 {
            font-size: 12px !important;
          }
          
          .coursesCard__content {
            padding: 0 20px 15px 20px !important;
          }
        }
        
        @media (max-width: 576px) {
          .pricing-tiers-container {
            padding: 0 10px 10px 10px;
          }
          
          .tier-header {
            padding: 12px 12px 6px 12px;
          }
          
          .tier-title {
            font-size: 15px !important;
          }
          
          .tier-price {
            font-size: 24px !important;
          }
          
          .tier-features {
            padding: 0 12px 12px 12px;
          }
          
          .tier-feature {
            font-size: 11px !important;
            margin-bottom: 8px;
          }
          
          .tier-purchase-btn {
            margin: 0 12px 12px 12px;
            padding: 12px 24px !important;
            font-size: 12px !important;
            width: calc(100% - 24px);
          }
          
          .go-to-learning-btn {
            padding: 14px 28px !important;
            font-size: 13px !important;
          }
        }
        
        /* Grid column equal heights - Force all cards in a row to have same height */
        .row.y-gap-30 {
          display: flex !important;
          flex-wrap: wrap !important;
          margin-left: -10px !important;
          margin-right: -10px !important;
        }
        
        .row.y-gap-30 > div[class*="col-"] {
          display: flex !important;
        }
        
        .row.y-gap-30 > div[class*="col-"] > .text-decoration-none {
          display: flex !important;
          width: 100%;
        }
        
        .row.y-gap-30 .coursesCard {
          display: flex !important;
          flex-direction: column !important;
          height: 100% !important;
          width: 100%;
        }
        
        /* Reset equal heights on mobile - cards stack vertically */
        @media (max-width: 991px) {
          .row.y-gap-30 {
            display: block !important;
          }
          
          .row.y-gap-30 > div[class*="col-"] {
            display: block !important;
          }
          
          .row.y-gap-30 > div[class*="col-"] > .text-decoration-none {
            display: block !important;
          }
          
          .row.y-gap-30 .coursesCard {
            height: auto !important;
          }
        }
        
        /* Glass background effect */
        .main-content {
          background: #000000;
          min-height: 100vh;
          position: relative;
        }
        
        .main-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICAgIDxnIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGcgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjAzKSIgZmlsbC1ydWxlPSJub256ZXJvIj4KICAgICAgICAgICAgPGNpcmNsZSBjeD0iNyIgY3k9IjciIHI9IjciLz4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==');
          opacity: 0.4;
          pointer-events: none;
        }
        
        .content-wrapper {
          position: relative;
          z-index: 1;
        }
        
        .page-header {
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
        
        .page-header__title {
          color: white !important;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        .page-header__text {
          color: rgba(255, 255, 255, 0.8) !important;
        }
        
        /* Purchased Exam Styles */
        .purchased-exam-container {
          padding: 20px;
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          margin-top: auto;
        }
        
        .purchased-header {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .purchased-badge {
          display: inline-flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.95);
          color: #1a1a1a;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .full-access-section {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 30px 20px;
          text-align: center;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }
        
        .access-title {
          color: white !important;
          font-size: 20px !important;
          font-weight: 600 !important;
          margin-bottom: 8px !important;
        }
        
        .access-subtitle {
          color: rgba(255, 255, 255, 0.7) !important;
          font-size: 14px !important;
          margin-bottom: 25px !important;
          line-height: 1.5;
        }
        
        .go-to-learning-btn {
          display: inline-block;
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.8) 0%, 
            rgba(15, 15, 15, 0.7) 50%,
            rgba(0, 0, 0, 0.8) 100%
          ) !important;
          color: #ffffff !important;
          text-decoration: none !important;
          padding: 18px 36px !important;
          border-radius: 50px !important;
          font-weight: 700 !important;
          font-size: 15px !important;
          letter-spacing: 0.8px !important;
          text-transform: uppercase !important;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                      0 0 20px rgba(255, 255, 255, 0.2),
                      inset 0 2px 0 rgba(255, 255, 255, 0.15),
                      inset 0 -1px 0 rgba(0, 0, 0, 0.2);
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          cursor: pointer;
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          position: relative;
          overflow: hidden;
          min-width: 200px;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          will-change: transform, box-shadow;
        }
        
        .go-to-learning-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }
        
        .go-to-learning-btn:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.15) 0%, 
            rgba(0, 0, 0, 0.9) 50%,
            rgba(255, 255, 255, 0.1) 100%
          ) !important;
          color: #ffffff !important;
          transform: translateY(-6px) scale(1.08);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6), 
                      0 0 50px rgba(255, 255, 255, 0.3),
                      inset 0 2px 0 rgba(255, 255, 255, 0.25),
                      inset 0 -2px 0 rgba(0, 0, 0, 0.3);
          text-decoration: none !important;
          border: 2px solid rgba(255, 255, 255, 0.5) !important;
        }
        
        .go-to-learning-btn:focus {
          color: #ffffff !important;
          text-decoration: none !important;
          outline: none;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                      0 0 20px rgba(255, 255, 255, 0.2),
                      0 0 0 3px rgba(255, 255, 255, 0.3);
        }
        
        @media (max-width: 768px) {
          .purchased-exam-container {
            padding: 15px;
          }
          
          .full-access-section {
            padding: 20px 15px;
          }
          
          .access-title {
            font-size: 18px !important;
          }
          
          .access-subtitle {
            font-size: 13px !important;
          }
          
          .go-to-learning-btn {
            padding: 12px 24px !important;
            font-size: 14px !important;
          }
          
          .purchased-badge {
            font-size: 12px;
            padding: 6px 12px;
            background: rgba(255, 255, 255, 0.95);
            color: #1a1a1a;
          }
        }

        /* Enhanced Shine Effect */
        .shine-effect {
          position: absolute;
          top: 0;
          left: -120%;
          width: 120%;
          height: 100%;
          background: linear-gradient(
            110deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.1) 45%,
            rgba(255, 255, 255, 0.3) 50%, 
            rgba(255, 255, 255, 0.1) 55%,
            transparent 100%
          );
          transition: left 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          border-radius: 28px;
          transform: skewX(-15deg);
          opacity: 0;
        }

        .coursesCard:hover .shine-effect {
          left: 120% !important;
          opacity: 1;
        }

        .tier-purchase-btn .button-shine {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.2) 45%,
            rgba(255, 255, 255, 0.4) 50%, 
            rgba(255, 255, 255, 0.2) 55%,
            transparent 100%
          );
          transition: left 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          border-radius: 50px;
          transform: skewX(-20deg);
          opacity: 0;
        }

        .tier-purchase-btn:hover .button-shine {
          left: 100% !important;
          opacity: 1;
        }

        /* Performance optimizations */
        .coursesCard,
        .tier-purchase-btn,
        .go-to-learning-btn {
          backface-visibility: hidden;
          perspective: 1000px;
        }

        /* AOS Override - Ensure elements are visible by default */
        [data-aos="fade-up"] {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        
        [data-aos="fade-up"].aos-animate {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        
        /* Force visibility on first load */
        .exams-grid-loaded [data-aos] {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }

        /* Enhanced keyframe animations */
        @keyframes cardFloat {
          0%, 100% { 
            transform: translateY(0px);
          }
          50% { 
            transform: translateY(-5px);
          }
        }

        @keyframes buttonGlow {
          0%, 100% {
            box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                        0 0 20px rgba(255, 255, 255, 0.2),
                        inset 0 2px 0 rgba(255, 255, 255, 0.15),
                        inset 0 -1px 0 rgba(0, 0, 0, 0.2);
          }
          50% {
            box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6),
                        0 0 30px rgba(255, 255, 255, 0.3),
                        inset 0 2px 0 rgba(255, 255, 255, 0.2),
                        inset 0 -1px 0 rgba(0, 0, 0, 0.3);
          }
        }

        /* Floating animation for cards */
        .coursesCard {
          animation: cardFloat 6s ease-in-out infinite;
        }

        .coursesCard:nth-child(2n) {
          animation-delay: 2s;
        }

        .coursesCard:nth-child(3n) {
          animation-delay: 4s;
        }
        
      `}</style>
      <MetaComponent meta={metadata} />
      <Preloader />

      <div className='content-wrapper js-content-wrapper overflow-hidden'>
        {/* Page Header */}
        <section className='page-header'>
          <div className='container'>
            <div className='row justify-center text-center'>
              <div className='col-xl-6 col-lg-8'>
                <h1 className='page-header__title'>
                  {selectedCourseSlug && (selectedCourse?.title || 'Selected Course')}
                  {!selectedCourseSlug && contentType === 'materials' && 'Study Materials'}
                  {!selectedCourseSlug && contentType === 'tests' && 'Test Papers'}
                  {!selectedCourseSlug && !contentType && 'All Exams'}
                </h1>
                <p className='page-header__text'>
                  {selectedCourseSlug &&
                    'Showing only the course selected from the home carousel.'}
                  {!selectedCourseSlug && contentType === 'materials' &&
                    'Access comprehensive study materials, notes, and resources for exam preparation.'}
                  {!selectedCourseSlug && contentType === 'tests' &&
                    'Practice with mock tests, previous year questions, and full-length test papers.'}
                  {!selectedCourseSlug && !contentType &&
                    'Choose from our comprehensive collection of exams and start your preparation journey.'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Exams Grid Section */}
        <section className='layout-pt-md layout-pb-md'>
          <div className='container'>
            {/* Content Type Filter Banner */}
            {contentType && (
              <div className='row mb-4'>
                <div className='col-12'>
                  <div
                    className='alert alert-info d-flex align-items-center justify-content-between'
                    style={{
                      background: 'rgba(66, 133, 244, 0.15)',
                      backdropFilter: 'blur(15px)',
                      WebkitBackdropFilter: 'blur(15px)',
                      border: '1px solid rgba(66, 133, 244, 0.3)',
                      borderRadius: '12px',
                      padding: '1rem 1.5rem',
                      color: 'rgba(255, 255, 255, 0.95)',
                    }}
                  >
                    <div>
                      <strong>
                        {contentType === 'materials' &&
                          '📚 Viewing Study Materials'}
                        {contentType === 'tests' && '📝 Viewing Test Papers'}
                      </strong>
                      <span className='ms-2'>
                        - Select an exam to access{' '}
                        {contentType === 'materials' ? 'materials' : 'tests'}
                      </span>
                    </div>
                    <Link
                      to='/exams'
                      className='btn btn-sm align-self-end align-self-sm-auto'
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          'rgba(255, 255, 255, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          'rgba(255, 255, 255, 0.2)';
                      }}
                    >
                      View All Exams
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {(loading || purchaseLoading) && (
              <div className='row justify-center'>
                <div className='col-lg-6'>
                  <div className='text-center py-60'>
                    <div
                      className='spinner-border text-primary mb-20'
                      role='status'
                    >
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    <h5 className='mb-10'>Loading...</h5>
                    <p className='text-light-1'>
                      Please wait while we fetch the exam information.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error State */}
            {!loading && error && (
              <div className='row justify-center'>
                <div className='col-lg-6'>
                  <div className='text-center py-60'>
                    <FontAwesomeIcon
                      icon={faGraduationCap}
                      size='3x'
                      className='text-danger mb-20'
                    />
                    <h5 className='mb-10'>Error Loading Exams</h5>
                    <p className='text-light-1 mb-20'>{error}</p>
                    <button
                      onClick={() => window.location.reload()}
                      className='button -md -outline-purple-1 text-purple-1'
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Exams Grid */}
            {!loading && !error && displayedExams.length > 0 && (
              <div className='row y-gap-30'>
                {displayedExams.map((exam, index) => (
                  <div
                    key={exam._id || exam.slug}
                    className='col-lg-4 col-md-6 px-20 py-20'
                  >
                    <Link
                      to={`/exam/${exam.slug}`}
                      className='text-decoration-none'
                    >
                      <div
                        className='coursesCard -type-1 rounded-12 bg-dark shadow-3 h-100 transition-all duration-300'
                        data-aos='fade-up'
                        data-aos-duration='600'
                        data-aos-delay={index * 100}
                        data-aos-offset='50'
                        data-aos-once='true'
                      >
                        {/* Shine Effect */}
                        <div className='shine-effect'></div>
                        {/* Card Header */}
                        <div className='coursesCard__header p-30 pb-20'>
                          <div className='d-flex align-items-center mb-20'>
                            <div className='coursesCard__icon size-50 rounded-full d-flex align-items-center justify-content-center mr-15'>
                              <FontAwesomeIcon
                                icon={getIconComponent(exam.icon)}
                                size='lg'
                              />
                            </div>
                            <div className='flex-grow-1'>
                              <h4 className='coursesCard__title text-18 lh-1 fw-500 mb-1'>
                                {exam.title}
                              </h4>
                              <span className='text-14 text-light-1'>
                                Exam Preparation
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Card Content */}
                        <div className='coursesCard__content'>
                          <p className='coursesCard__text text-14 lh-18 text-light-1 mb-10'>
                            {exam.description ||
                              'Discover the intricacies of life sciences with our biology subject.'}
                          </p>
                          {purchaseInfo[exam._id]?.userStatus?.isExpired && (
                            <p className='text-warning text-13 lh-18 mb-10'>
                              {purchaseInfo[exam._id].userStatus.message || RENEWAL_MESSAGE}
                            </p>
                          )}

                          {/* Display exam features under description */}
                          {exam.features && exam.features.length > 0 && (
                            <div className='exam-features-list mt-15'>
                              {exam.features.map((feature, featureIndex) => (
                                <div
                                  key={featureIndex}
                                  className='tier-feature mb-8'
                                >
                                  <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className='tier-feature-icon'
                                  />
                                  <span>{feature}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Check if user has purchased this exam (admin has full access without purchase) */}
                        {loggedIn &&
                          purchaseInfo[exam._id] &&
                          !purchaseInfo[exam._id].exam.isFree && (
                            <>
                              {/* Check purchase type and show appropriate interface */}
                              {(purchaseInfo[exam._id].userStatus?.hasPurchased || loggedInUser?.role === 'admin') ? (
                                <>
                                  {(purchaseInfo[exam._id].userStatus?.purchaseType === 'full' || loggedInUser?.role === 'admin') ? (
                                    /* Full access purchased - show button only */
                                    <div className='pricing-tiers-container'>
                                      <Link
                                        to={`/exam/${exam.slug}`}
                                        className='tier-purchase-btn full-exam-btn simple-purchase-btn purchased'
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className='button-shine'></div>
                                        Go to Learning
                                        <span style={{ marginLeft: '4px' }}>
                                          →
                                        </span>
                                      </Link>
                                    </div>
                                  ) : (
                                    /* Tests-only purchased - show current access + upgrade option */
                                    <div className='pricing-tiers-container'>
                                      {/* Show current tests-only access */}
                                      <div className='mb-15'>
                                        <div className='pricing-tier-card tests-only purchased-tier'>
                                          <div className='tier-header'>
                                            <div className='tier-title'>
                                              Tests Only
                                            </div>
                                            <div>
                                              <span className='tier-price'>
                                                ✓ Purchased
                                              </span>
                                            </div>
                                          </div>

                                          <div className='tier-features'>
                                            <div className='tier-feature'>
                                              <FontAwesomeIcon
                                                icon={faCheckCircle}
                                                className='tier-feature-icon'
                                              />
                                              <span>
                                                {exam.testCount || '15+'}{' '}
                                                Full-length Tests
                                              </span>
                                            </div>
                                            <div className='tier-feature'>
                                              <FontAwesomeIcon
                                                icon={faCheckCircle}
                                                className='tier-feature-icon'
                                              />
                                              <span>Performance Analytics</span>
                                            </div>
                                            <div className='tier-feature'>
                                              <FontAwesomeIcon
                                                icon={faCheckCircle}
                                                className='tier-feature-icon'
                                              />
                                              <span>Previous Year Papers</span>
                                            </div>
                                          </div>

                                          <Link
                                            to={`/exam/${exam.slug}`}
                                            className='tier-purchase-btn tests-only-btn purchased'
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <div className='button-shine'></div>
                                            Start Tests
                                            <span
                                              style={{
                                                marginLeft: '8px',
                                                fontSize: '16px',
                                              }}
                                            >
                                              →
                                            </span>
                                          </Link>
                                        </div>
                                      </div>

                                      {/* Show upgrade to full exam section */}
                                      <div className='mb-15'>
                                        {/* Price Display */}
                                        <div className='price-display-section'>
                                          <div className='price-info-left'>
                                            <div className='price-main-row'>
                                              <span className='price-current'>
                                                ₹
                                                {
                                                  getPricingTiers(
                                                    exam,
                                                    purchaseInfo[exam._id]
                                                  )[0].price
                                                }
                                              </span>
                                              {getPricingTiers(
                                                exam,
                                                purchaseInfo[exam._id]
                                              )[0].originalPrice && (
                                                <span className='price-original'>
                                                  ₹
                                                  {
                                                    getPricingTiers(
                                                      exam,
                                                      purchaseInfo[exam._id]
                                                    )[0].originalPrice
                                                  }
                                                </span>
                                              )}
                                            </div>
                                            {getPricingTiers(
                                              exam,
                                              purchaseInfo[exam._id]
                                            )[0].originalPrice && (
                                              <span className='price-savings'>
                                                You save ₹
                                                {getPricingTiers(
                                                  exam,
                                                  purchaseInfo[exam._id]
                                                )[0].originalPrice -
                                                  getPricingTiers(
                                                    exam,
                                                    purchaseInfo[exam._id]
                                                  )[0].price}
                                              </span>
                                            )}
                                          </div>
                                          <span className='price-discount-badge upgrade-badge'>
                                            UPGRADE
                                          </span>
                                        </div>

                                        {/* Action Button */}
                                        <button
                                          className={`tier-purchase-btn full-exam-btn simple-purchase-btn upgrade ${processingPayment ? 'processing' : ''}`}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handlePurchaseClick(exam, 'full');
                                          }}
                                          disabled={processingPayment}
                                        >
                                          <div className='button-shine'></div>
                                          {processingPayment ? (
                                            <>
                                              <div
                                                className='spinner-border spinner-border-sm me-2'
                                                role='status'
                                              >
                                                <span className='visually-hidden'>
                                                  Loading...
                                                </span>
                                              </div>
                                              Processing...
                                            </>
                                          ) : (
                                            <>
                                              Upgrade Now
                                              <span
                                                style={{ marginLeft: '4px' }}
                                              >
                                                →
                                              </span>
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                /* Show simple purchase button for non-purchased exams */
                                <div className='pricing-tiers-container'>
                                  {(() => {
                                    const tiers = getPricingTiers(
                                      exam,
                                      purchaseInfo[exam._id]
                                    );
                                    const fullExamTier = tiers.find(
                                      (t) => t.id === 'full'
                                    );
                                    const testsOnlyTier = tiers.find(
                                      (t) => t.id === 'tests'
                                    );

                                    return (
                                      <>
                                        {/* Full Exam Purchase Section - Show when type is not 'tests' */}
                                        {fullExamTier &&
                                          contentType !== 'tests' && (
                                            <div className='mb-15'>
                                              {/* Price Display */}
                                              <div className='price-display-section'>
                                                <div className='price-info-left'>
                                                  <div className='price-main-row'>
                                                    <span className='price-current'>
                                                      ₹{fullExamTier.price}
                                                    </span>
                                                    {fullExamTier.originalPrice && (
                                                      <span className='price-original'>
                                                        ₹
                                                        {
                                                          fullExamTier.originalPrice
                                                        }
                                                      </span>
                                                    )}
                                                  </div>
                                                  {fullExamTier.originalPrice && (
                                                    <span className='price-savings'>
                                                      You save ₹
                                                      {fullExamTier.originalPrice -
                                                        fullExamTier.price}
                                                    </span>
                                                  )}
                                                </div>
                                                {fullExamTier.discount && (
                                                  <span className='price-discount-badge'>
                                                    {fullExamTier.discount}% OFF
                                                  </span>
                                                )}
                                              </div>

                                              {/* Action Button */}
                                              <button
                                                className={`tier-purchase-btn full-exam-btn simple-purchase-btn stacked-action-btn ${processingPayment ? 'processing' : ''}`}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handlePurchaseClick(
                                                    exam,
                                                    'full'
                                                  );
                                                }}
                                                disabled={processingPayment}
                                              >
                                                <div className='button-shine'></div>
                                                {processingPayment ? (
                                                  <>
                                                    <div
                                                      className='spinner-border spinner-border-sm me-2'
                                                      role='status'
                                                    >
                                                      <span className='visually-hidden'>
                                                        Loading...
                                                      </span>
                                                    </div>
                                                    Processing...
                                                  </>
                                                ) : (
                                                  <>
                                                    {purchaseInfo[exam._id]?.userStatus?.isExpired ? 'Renew Access' : 'Buy Now'}
                                                  </>
                                                )}
                                              </button>
                                              <Link
                                                to={`/exam/${exam.slug}`}
                                                className='tier-purchase-btn simple-purchase-btn view-subjects-btn'
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                <div className='button-shine'></div>
                                                View Subjects
                                                <span
                                                  style={{
                                                    marginLeft: '4px',
                                                  }}
                                                >
                                                  →
                                                </span>
                                              </Link>
                                            </div>
                                          )}

                                        {/* Tests Only Section - Show when type is 'tests' */}
                                        {testsOnlyTier &&
                                          contentType === 'tests' && (
                                            <div className='mb-15'>
                                              {/* Section Title */}
                                              <div className='purchase-section-title'>
                                                {testsOnlyTier.title}
                                              </div>

                                              {/* Tests Only Features */}
                                              {testsOnlyTier.features &&
                                                testsOnlyTier.features.length >
                                                  0 && (
                                                  <div className='purchase-features-list'>
                                                    {testsOnlyTier.features
                                                      .slice(0, 3)
                                                      .map(
                                                        (
                                                          feature,
                                                          featureIndex
                                                        ) => (
                                                          <div
                                                            key={featureIndex}
                                                            className='tier-feature mb-8'
                                                          >
                                                            <FontAwesomeIcon
                                                              icon={
                                                                faCheckCircle
                                                              }
                                                              className='tier-feature-icon'
                                                            />
                                                            <span>
                                                              {feature}
                                                            </span>
                                                          </div>
                                                        )
                                                      )}
                                                  </div>
                                                )}

                                              {/* Price Display */}
                                              <div className='price-display-section'>
                                                <div className='price-info-left'>
                                                  <div className='price-main-row'>
                                                    <span className='price-current'>
                                                      ₹{testsOnlyTier.price}
                                                    </span>
                                                    {testsOnlyTier.originalPrice && (
                                                      <span className='price-original'>
                                                        ₹
                                                        {
                                                          testsOnlyTier.originalPrice
                                                        }
                                                      </span>
                                                    )}
                                                  </div>
                                                  {testsOnlyTier.originalPrice && (
                                                    <span className='price-savings'>
                                                      You save ₹
                                                      {testsOnlyTier.originalPrice -
                                                        testsOnlyTier.price}
                                                    </span>
                                                  )}
                                                </div>
                                                {testsOnlyTier.discount && (
                                                  <span className='price-discount-badge'>
                                                    {testsOnlyTier.discount}%
                                                    OFF
                                                  </span>
                                                )}
                                              </div>

                                              {/* Action Button */}
                                              <button
                                                className={`tier-purchase-btn tests-only-btn simple-purchase-btn ${processingPayment ? 'processing' : ''}`}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handlePurchaseClick(
                                                    exam,
                                                    'tests'
                                                  );
                                                }}
                                                disabled={processingPayment}
                                              >
                                                <div className='button-shine'></div>
                                                {processingPayment ? (
                                                  <>
                                                    <div
                                                      className='spinner-border spinner-border-sm me-2'
                                                      role='status'
                                                    >
                                                      <span className='visually-hidden'>
                                                        Loading...
                                                      </span>
                                                    </div>
                                                    Processing...
                                                  </>
                                                ) : (
                                                  <>
                                                    {purchaseInfo[exam._id]?.userStatus?.isExpired ? 'Renew Access' : 'Buy Tests Only'}
                                                    <span
                                                      style={{
                                                        marginLeft: '4px',
                                                      }}
                                                    >
                                                      →
                                                    </span>
                                                  </>
                                                )}
                                              </button>
                                            </div>
                                          )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </>
                          )}

                        {/* Free Exam Status */}
                        {loggedIn &&
                          purchaseInfo[exam._id] &&
                          purchaseInfo[exam._id].exam.isFree && (
                            <div className='pricing-tiers-container'>
                              <div className='purchase-status free-exam-status'>
                                <div className='d-flex align-items-center justify-center gap-2 p-15 bg-info bg-opacity-10 rounded-12'>
                                  <FontAwesomeIcon
                                    icon={faStar}
                                    className='text-white text-16'
                                  />
                                  <span className='text-white text-16 fw-700'>
                                    ✨ Free Exam - Full Access
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                        {/* Card Footer */}
                        <div className='coursesCard__footer -auto'>
                          <div className='d-flex align-items-center justify-between'>
                            <span
                              className='text-14 fw-600'
                              style={{ color: 'rgba(255, 255, 255, 0.9)' }}
                            >
                              {loggedIn &&
                              purchaseInfo[exam._id]?.userStatus?.canAccess ? (
                                <>
                                  Start Preparation
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    →
                                  </span>
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon
                                    icon={faLock}
                                    className='me-2'
                                  />
                                  View Details
                                </>
                              )}
                            </span>
                            <div className='d-flex align-items-center'>
                              {loggedIn &&
                              purchaseInfo[exam._id]?.userStatus?.canAccess ? (
                                <span className='badge bg-success bg-opacity-20 text-success px-8 py-3 text-10 fw-600 rounded-pill'>
                                  UNLOCKED
                                </span>
                              ) : (
                                <span className='badge bg-warning bg-opacity-20 text-warning px-8 py-3 text-10 fw-600 rounded-pill'>
                                  LOCKED
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && displayedExams.length === 0 && (
              <div className='row justify-center'>
                <div className='col-lg-6'>
                  <div className='text-center py-60'>
                    <FontAwesomeIcon
                      icon={faGraduationCap}
                      size='3x'
                      className='text-light-3 mb-20'
                    />
                    <h5 className='mb-10'>
                      {selectedCourseSlug ? 'Selected Course Not Found' : 'No Exams Available'}
                    </h5>
                    <p className='text-light-1'>
                      {selectedCourseSlug
                        ? 'The course selected from the carousel is no longer available.'
                        : 'There are currently no exams available. Please check back later.'}
                    </p>
                    {selectedCourseSlug && (
                      <Link
                        to='/exams'
                        className='button -md -outline-purple-1 text-purple-1 mt-20'
                      >
                        View All Exams
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ExamsListPage;
